import React, { useState } from 'react';
import { Box, Typography, Button, AppBar, Toolbar, Avatar, IconButton, Menu, MenuItem, Tooltip, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import LogoutIcon from '@mui/icons-material/Logout';
import { useUserContext } from '@root/providers/user_context';
import { useUserProfileRedux } from '../../hooks/useUserProfileRedux';
import { UserProfileCacheService } from '../services/UserProfileCacheService';
import { SessionStorageService } from '../services/SessionStorageService';

const AccessDeniedContainer = styled(Box)({
  
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#000000', // Black background
  overflow: 'hidden',
});

const ContentContainer = styled(Box)({
  position: 'relative',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  overflow: 'hidden'
});

const BackgroundImage = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%', // Smaller upper portion for the pattern
  backgroundImage: 'url(/assets/rxo_background_4.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center top',
  backgroundRepeat: 'no-repeat',
  opacity: 1, // Fully visible
  zIndex: 0,
});

const ContentBox = styled(Box)({
  position: 'relative',
  zIndex: 1,
  textAlign: 'right',
  color: '#ffffff',
  maxWidth: '600px',
  padding: '40px',
});

const Title = styled(Typography)({
  fontSize: '1.75rem',
  fontWeight: 700,
  marginBottom: '16px',
  color: '#ffffff',
  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
});

const Subtitle = styled(Typography)({
  fontSize: '1rem',
  marginBottom: '32px',
  color: '#10b981', // Green color matching the screenshot
  fontWeight: 500,
});

const Message = styled(Typography)({
  fontSize: '0.875rem',
  marginBottom: '24px',
  color: '#e5e7eb',
  lineHeight: 1.6,
});

const StyledButton = styled(Button)({
  backgroundColor: 'transparent',
  color: '#10b981',
  border: '2px solid #10b981',
  padding: '8px 24px',
  fontSize: '0.875rem',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '4px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#10b981',
    color: '#000000',
    borderColor: '#10b981',
  },
});

interface AccessDeniedProps {
  userEmail?: string;
  onContactSupport?: () => void;
  message?: string;
  showRetry?: boolean;
}

export const AccessDenied: React.FC<AccessDeniedProps> = ({ userEmail, onContactSupport, message, showRetry = false }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const open = Boolean(anchorEl);
  const { signOut } = useUserContext();
  const { clearProfileCache, refetchProfile } = useUserProfileRedux();

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      // Clear cache and reset failure count, then retry
      clearProfileCache();
      await refetchProfile(true); // force refresh
      // Page will automatically update if profile loads successfully
    } catch (error) {
      console.error('Retry failed:', error);
    } finally {
      setIsRetrying(false);
    }
  };

  const handleContactSupport = () => {
    if (onContactSupport) {
      onContactSupport();
    } else {
      // Default behavior - open email client
      window.location.href = 'mailto:support@rxo.com?subject=Access Request for MT Onboarding Tool';
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle logout - same as NavBar
  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      setAnchorEl(null); // Close the menu first

      // Clear all user profile caches (Redux and memory cache)
      clearProfileCache();
      UserProfileCacheService.clearCache();

      // Use OIDC to sign out (this will redirect away from page)
      signOut();

    } catch (error) {
      console.error('AccessDenied: Logout error, forcing manual cleanup', error);

      // Fallback: force logout and redirect
      clearProfileCache();
      UserProfileCacheService.clearCache();
      SessionStorageService.clearAll(); // Use SessionStorageService for proper cleanup
      sessionStorage.removeItem('userEmail');
      sessionStorage.removeItem('isLoggedIn');
      sessionStorage.removeItem('oidc_callback_processed');
      sessionStorage.removeItem('is_logging_out');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('isLoggedIn');
      window.location.href = window.location.origin;
    }
    // Note: No finally block - we're redirecting away, so no need to reset state
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (userEmail) {
      const namePart = userEmail.split('@')[0];
      const parts = namePart.split('.');
      if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
      }
      return namePart.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Get user name from email
  const getUserName = () => {
    if (userEmail) {
      const namePart = userEmail.split('@')[0];
      const parts = namePart.split('.');
      if (parts.length >= 2) {
        return parts.map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
      }
      return namePart.charAt(0).toUpperCase() + namePart.slice(1);
    }
    return 'Unknown user';
  };

  return (
    <AccessDeniedContainer>
      {/* Simple Navigation Bar - Logo and Avatar only */}
      <AppBar
        position="static"
        elevation={0}
        color="inherit"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: '#ffffff',
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Box
              component="img"
              src='/rxo_connect_logo.png'
              alt="RXO Connect Logo"
              sx={{
                height: 32,
                width: 'auto',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Profile" arrow>
              <IconButton onClick={handleClick} size="small" color="inherit">
                <Avatar alt="User Avatar" variant="circular">
                  {getUserInitials()}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleClose}>
          {getUserName()}
        </MenuItem>
        <MenuItem onClick={handleClose}>
          {userEmail || 'Unknown email'}
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={handleLogout}
          disabled={isLoggingOut}
        >
          <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
          {isLoggingOut ? 'Logging out...' : 'Logout'}
        </MenuItem>
      </Menu>

      {/* Content Area */}
      <ContentContainer>
        <BackgroundImage />

        <ContentBox>
          <Title>
            Welcome to RXO Connect Admin Console - MT Onboarding Tool
          </Title>

          <Subtitle>
            Don't have an account or access?
          </Subtitle>

          <Message>
            {message ? (
              <>
                {message}
                {showRetry && (
                  <>
                    <br /><br />
                    <Button 
                      variant="contained" 
                      onClick={handleRetry}
                      disabled={isRetrying}
                      sx={{ mt: 2 }}
                    >
                      {isRetrying ? 'Retrying...' : 'Retry Connection'}
                    </Button>
                  </>
                )}
              </>
            ) : userEmail ? (
              <>
                Your account <strong>{userEmail}</strong> does not have access to the MT Onboarding Tool.
                <br /><br />
                Please contact your administrator or support team to request access.
              </>
            ) : (
              <>
                You do not have access to the MT Onboarding Tool.
                <br /><br />
                Please contact your administrator or support team to request access.
              </>
            )}
          </Message>

        
        </ContentBox>
      </ContentContainer>
    </AccessDeniedContainer>
  );
};

export default AccessDenied;
